import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import * as Icons from "lucide-react"

function ItemsStats({ data, mantisBusiness }) {
  const [stats, setStats] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const getIcon = (iconName) => {
    const iconNamePascal = iconName.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join('')
    
    const Icon = Icons[iconNamePascal] || Icons.Package
    return <Icon className="h-5 w-5" />
  }

  const getIconColor = (type) => {
    switch (type) {
      case 'total': return 'text-blue-500';
      case 'low_stock': return 'text-red-500';
      case 'normal_stock': return 'text-green-500';
      case 'high_stock': return 'text-purple-500';
      case 'orders': return 'text-orange-500';
      default: return 'text-gray-500';
    }
  }

  const formatStatValue = (value) => {
    // Asegurar que es un número válido
    if (value === null || value === undefined) {
      return '0';
    }
    
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return '0';
    }
    
    // Formatear números con separadores de miles
    if (numValue >= 1000000) {
      return (numValue / 1000000).toFixed(1) + 'M';
    } else if (numValue >= 1000) {
      return (numValue / 1000).toFixed(1) + 'K';
    } else {
      return Math.round(numValue).toLocaleString('es-CO');
    }
  };

  const safeParseNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return isNaN(value) ? 0 : value;
    
    // Si es string, intentar convertir
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const calculateStats = () => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [
        {
          id: 'total_items',
          name: 'Total Items',
          icon: 'package',
          color: 'blue',
          value: 0,
          type: 'total'
        },
        {
          id: 'low_stock',
          name: 'Stock Bajo',
          icon: 'alert-triangle',
          color: 'red',
          value: 0,
          type: 'low_stock'
        },
        {
          id: 'normal_stock',
          name: 'Stock Normal',
          icon: 'check-circle',
          color: 'green',
          value: 0,
          type: 'normal_stock'
        },
        {
          id: 'high_stock',
          name: 'Stock Alto',
          icon: 'trending-up',
          color: 'purple',
          value: 0,
          type: 'high_stock'
        },
        {
          id: 'total_to_order',
          name: 'Cantidad Total a Ordenar',
          icon: 'shopping-cart',
          color: 'orange',
          value: 0,
          type: 'orders'
        }
      ];
    }

    console.log('🔢 Calculando estadísticas con datos:', data.length, 'items');

    const totalItems = data.length;
    const lowStockItems = data.filter(item => 
      item.stock_status === 'low' || item.stock_status === 'insufficient'
    ).length;
    const normalStockItems = data.filter(item => 
      item.stock_status === 'normal' || item.stock_status === 'sufficient'
    ).length;
    const highStockItems = data.filter(item => 
      item.stock_status === 'high' || item.stock_status === 'partial'
    ).length;
    
    // Calcular total de cantidad a ordenar con verificación de números
    const totalQuantityToOrder = data.reduce((sum, item) => {
      const quantity = safeParseNumber(item.quantity_to_order);
      return sum + quantity;
    }, 0);

    // Calcular total de stock actual
    const totalCurrentStock = data.reduce((sum, item) => {
      const stock = safeParseNumber(item.current_stock);
      return sum + stock;
    }, 0);

    const statsResult = [
      {
        id: 'total_items',
        name: 'Total Items',
        icon: 'package',
        color: 'blue',
        value: totalItems,
        type: 'total'
      },
      {
        id: 'low_stock',
        name: 'Stock Bajo',
        icon: 'alert-triangle',
        color: 'red',
        value: lowStockItems,
        type: 'low_stock'
      },
      {
        id: 'normal_stock',
        name: 'Stock Normal',
        icon: 'check-circle',
        color: 'green',
        value: normalStockItems,
        type: 'normal_stock'
      },
      {
        id: 'high_stock',
        name: 'Stock Alto',
        icon: 'trending-up',
        color: 'purple',
        value: highStockItems,
        type: 'high_stock'
      },
      {
        id: 'total_to_order',
        name: 'Cantidad Total a Ordenar',
        icon: 'shopping-cart',
        color: 'orange',
        value: totalQuantityToOrder,
        type: 'orders'
      }
    ];

    console.log('📊 Estadísticas calculadas:', statsResult);
    return statsResult;
  };

  useEffect(() => {
    setIsLoading(true);
    
    // Pequeño delay para mostrar el loading
    setTimeout(() => {
      const calculatedStats = calculateStats();
      setStats(calculatedStats);
      setIsLoading(false);
    }, 100);
    
  }, [data]);

  const StatItem = ({ name, value, icon, color, type }) => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`
        flex flex-col items-center justify-center gap-3 px-4 py-6
        transition-all duration-300
        hover:bg-slate-50 dark:hover:bg-slate-700/50
        border-r border-slate-200 dark:border-slate-700 last:border-r-0
      `}
    >
      <div className={`
        flex items-center justify-center w-12 h-12 rounded
        bg-gradient-to-br from-slate-50 to-slate-100
        dark:from-slate-800 dark:to-slate-900
        shadow-sm border border-slate-200 dark:border-slate-700
      `}>
        <span className={`${getIconColor(type)}`}>
          {getIcon(icon)}
        </span>
      </div>
      <div className="flex-1 min-w-0 text-center">
        <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
          {name}
        </p>
        <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          {formatStatValue(value)}
        </p>
      </div>
    </motion.div>
  );

  const EmptyState = () => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col items-center justify-center py-12 px-4 text-center"
    >
      <div className="w-16 h-16 mb-4 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
        <Icons.Package className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
        No hay estadísticas disponibles
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
        Las estadísticas aparecerán cuando haya items disponibles para analizar.
      </p>
    </motion.div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mb-6"
    >
      <div className="bg-white dark:bg-slate-800 rounded shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
          <div className="flex items-center gap-3">
            <Icons.BarChart3 className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Estadísticas de Items
            </h3>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {data?.length || 0} items analizados
          </div>
        </div>
        
        {isLoading ? (
          <div className="grid grid-cols-5">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse flex flex-col items-center justify-center gap-2 px-4 py-6 border-r border-slate-200 dark:border-slate-700 last:border-r-0">
                <div className="w-12 h-12 rounded bg-slate-200 dark:bg-slate-700 mb-2" />
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-20 mb-1" />
                <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-12" />
              </div>
            ))}
          </div>
        ) : stats.length > 0 ? (
          <div className="grid grid-cols-5">
            {stats.map((stat) => (
              <StatItem 
                key={stat.id}
                name={stat.name}
                value={stat.value}
                icon={stat.icon}
                color={stat.color}
                type={stat.type}
              />
            ))}
          </div>
        ) : (
          <EmptyState />
        )}
      </div>
    </motion.div>
  );
}

export default ItemsStats; 